import { useState } from "react";
import { X, Hospital, LogOut } from "lucide-react";
import api from "@/api";
import ModalShell from "@/components/shared/ModalShell";
import { toast } from "sonner";

// Statut administratif du dossier : hospitalisation confirmée / fin d'hospitalisation. Jamais automatique.
export default function DossierStatusModal({ patient, onClose, onDone }) {
  const hospitalized = patient?.dossierStatus === "hospitalise";
  const target = hospitalized ? "actif" : "hospitalise";
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const minLen = hospitalized ? 0 : 5;

  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(`/patients/${patient.id}/dossier-status`, { dossierStatus: target, reason });
      toast.success(hospitalized ? "Fin d'hospitalisation enregistrée" : "Hospitalisation confirmée enregistrée");
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell testId="dossier-status-modal">
      <div className={`p-6 border-b ${hospitalized ? "bg-slate-50" : "bg-rose-50"}`}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            {hospitalized ? <LogOut className="w-5 h-5 text-slate-600" /> : <Hospital className="w-5 h-5 text-rose-600" />}
            {hospitalized ? "Fin d'hospitalisation" : "Déclarer une hospitalisation confirmée"}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg" data-testid="dossier-modal-close">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>
        <p className="mt-2 text-xs text-slate-600">
          {hospitalized
            ? "Le dossier repasse en suivi actif. Le statut clinique n'est pas modifié."
            : "Statut administratif, indépendant du statut clinique. À déclarer uniquement si l'hospitalisation est effective."}
        </p>
      </div>
      <div className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            {hospitalized ? "Commentaire (facultatif)" : "Motif / lieu de l'hospitalisation *"}
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            data-testid="dossier-modal-reason"
            placeholder={hospitalized ? "Ex. : sortie le 12/10, retour à domicile" : "Ex. : admission CHU, service d'oncologie, neutropénie fébrile"}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-500 outline-none"
          />
        </div>
        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-slate-600 font-medium hover:bg-slate-50">Annuler</button>
          <button
            onClick={submit}
            disabled={saving || reason.trim().length < minLen}
            data-testid="dossier-modal-confirm"
            className={`flex-1 py-2.5 text-white rounded-xl font-medium disabled:opacity-50 ${hospitalized ? "bg-slate-700 hover:bg-slate-800" : "bg-rose-600 hover:bg-rose-700"}`}
          >
            {saving ? "..." : hospitalized ? "Confirmer la sortie" : "Confirmer l'hospitalisation"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
